import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import dayjs from "dayjs";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite } from "@/lib/site";

export const BlogSection = () => {
  const { content } = useSite();
  const posts = (content.posts || []).filter((p) => p.published !== false).slice(0, 3);
  if (!posts.length) return null;

  return (
    <section id="blog" data-testid="blog-section" className="bg-cream/60 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>From the journal</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink">
              Articles for <span className="italic text-terra">new mothers.</span>
            </h2>
          </div>
          <Link
            data-testid="blog-view-all"
            to="/blog"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-6 py-3 font-outfit text-sm font-semibold text-ink transition-colors duration-300 hover:border-terra hover:text-terra"
          >
            View all articles <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {posts.map((p, i) => (
            <Reveal key={p.id || p.slug} delay={i * 0.1}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${i}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-deep"
              >
                {p.cover_image ? (
                  <img src={p.cover_image} alt={p.title} className="aspect-video w-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-plum">
                    <img src="/assets/mother-baby-art.png" alt="" className="h-24 w-24 opacity-40 invert" />
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  {p.published_at && (
                    <p className="flex items-center gap-1.5 text-[11px] font-outfit uppercase tracking-widest text-faint">
                      <CalendarDays className="h-3 w-3" /> {dayjs(p.published_at).format("D MMM YYYY")}
                    </p>
                  )}
                  <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-ink transition-colors group-hover:text-terra">
                    {p.title}
                  </h3>
                  {p.excerpt && <p className="mt-2 flex-1 text-sm leading-relaxed text-plumsoft line-clamp-3">{p.excerpt}</p>}
                  <span className="mt-4 inline-flex items-center gap-1 font-outfit text-xs font-semibold uppercase tracking-wider text-terra">
                    Read article <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
